/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
// Names of markers to be marked in navigation.mark
// By convention the name used in measure is <marker name>_MEASURE

export const PREVIEW_JS_LOAD_START = "previewJSLoadStartTime";
export const PREVIEW_JS_LOAD_END = "previewJSLoadEndTime";

export const VIEWER_INIT = "viewerInitTime";
export const PREVIEW_INIT = "previewInitTime";
export const FIRST_PAGE_LOADED = "timeTillFirstPage";
export const FIRST_PAGE_RENDERED = "timeToViewFirstPage";

export const GET_METADATA_START = "getMetadataStart";
export const GET_METADATA_END = "getMetadataEnd";

export const PAGE_LOAD = "pageLoadTimes";
export const PAGE_LOAD_START_TIME = "pageLoadStartTime";
export const PAGE_LOAD_END_TIME = "pageLoadEndTime";

export const AJS_FIRST_PAGE_LOAD_START_TIME = "ajsFirstPageLoadStart";

export const FIRST_PAGE_RELOAD_TIME = "firstPageReloadTime";

export const EXTERNAL_META_DATA_END_TIME = "externalMetadataEndTime";
export const EXTERNAL_PAGE_LOAD_END_TIME = "externalPageLoadEndTime";
export const EXTERNAL_PAGE_LOAD_START_TIME = "externalPageLoadStartTime";

export const FIRST_RASTER_LOADED = "firstRasterLoadedTime";

export const PAGE_PARTIALLY_LOADED = "pagePartiallyLoaded";
export const FIRST_TILE_LOADED_FOR_PAGE = "firstTileLoadedForPage";
export const FIRST_PROGRESSIVE_RENDERING_RESPONSE_FOR_PAGE = "firstProgressiveRenderingResponseForPage";

export const DOC_JS_ENGINE_INIT_START = "docJsEngineInitStart";
export const DOC_JS_ENGINE_INIT_END = "docJsEngineInitEnd";
export const PAGE_JS_ENGINE_INIT_START = "pageJsEngineInitStart";
export const PAGE_JS_ENGINE_INIT_END = "pageJsEngineInitEnd";
export const EXECUTE_DOC_OPEN_ACTIONS_START = "executeDocOpenActionsStart";
export const EXECUTE_DOC_OPEN_ACTIONS_END = "executeDocOpenActionsEnd";

export const externalMarkers = [
  { name: "viewerExperienceStartTime" },
  { name: "dc-viewer.load" },
  { name: "viewer-alm-configurator-wait-ims-ready" },
  { name: "viewer-alm-configurator-start" },
  { name: "dc-viewer-alm-configurator.load" },
  { name: "viewer-alm-configurator-init" },
  { name: "alm:APP_LOAD" },
  { name: "alm:PRE_LOAD" },
  { name: "DCAppLauncher-domLoaded" },
  { name: "DCAppLauncher__imsReady-resolved" },
  { name: "DCAppLauncher__downloadAssetURLs-start" },
  { name: "DCAppLauncher__downloadAssetURLs-resolved" },
  { name: "DCAppLauncher__overridesReady-resolved" },
  { name: "DCWebApp__setSpectrumTheme-start" },
  { name: "DCWebApp__setSpectrumTheme-end" },
  { name: "DCWebApp-start" },
  { name: "DCWebApp-end" },
  { prefix: "provider_rendition_" },
  { prefix: "dropin_viewer_" },
  { prefix: "dropin_review_" },
  { prefix: "web_app_" },
  { prefix: "dropin_home2_" },
  { prefix: "preview_provider_" },
  { prefix: "comments_sdk_" }
];

// Specific to DC Web App
export const VIEWER_ALM_CONFIGURATOR_INIT = "viewer-alm-configurator-init";

// Regex for perf logging in dc web
export const perfRegexList = [
  { regex: /^.*dc-viewer-dropin\/.*\/bootstrap\.js$/, name: "viewer" },
  { regex: /^.*dc-rendition-provider\/.*\/rendition\.js$/, name: "rendition" },
  { regex: /^.*dc-web-app-dropin\/.*\/bootstrap\.js$/, name: "web-app-dropin" },
  {
    regex: /^.*bootstrap\?shareinfo=true$/,
    name: "bootstrap-call",
    shouldLogBreakdown: true,
    shouldLogDurationInAcrobatPerfEvent: true
  },
  { regex: /^.*uri\/download\?make_direct_storage_uri=true$/, name: "get-buffer-url-1", shouldLogBreakdown: true },
  { regex: /^.*\/track$/, name: "get-buffer-url-2", shouldLogBreakdown: true },
  { regex: /^.*s3\-accelerate\.amazonaws\.com.*$/, name: "download-buffer-1", shouldLogBreakdown: true },
  {
    regex: /^.*cdn\-sharing.*\.adobecc\.com\/content\/storage\/id\/((?!rendition).)*$/,
    name: "download-buffer-2",
    shouldLogBreakdown: true
  },
  {
    regex: /^.*cdn\-sharing.*\.adobecc\.com\/.*\/id\/.*;page=0;size=1200.*$/,
    name: "getACPPage0Rendition",
    shouldLogBreakdown: true,
    shouldLogSpeed: true,
    shouldLogDurationInAcrobatPerfEvent: true
  },
  {
    regex: /^.*dc-api.*\.adobecontent\.io.*\/rendition.*\&format\=jpg\&size\=1200$/,
    name: "getTrackReviewPage0Rendition",
    shouldLogDurationInAcrobatPerfEvent: true
  },
  { regex: /^https:\/\/files\.[a-z\.]*\/api\/base_uris/, name: "send-and-track-url" },
  { regex: /^.*imslib\/imslib\.min\.js$/, name: "imsLibJs" },
  { regex: /^.*dc\/overrides\.js$/, name: "overridesJs" },
  { regex: /^.*viewerDropin-chunk.js$/, name: "viewerDropinChunk" },
  {
    regex: /^.*acp-aep-cs.*$/,
    name: "acpBufferCall",
    shouldLogBreakdown: true,
    shouldLogDurationInAcrobatPerfEvent: true
  },
  {
    regex: /^.*invitation\/.*\/asset\/asset\-0\/preview\?withDownloadUrl\=true$/,
    name: "uriInvitationCall",
    shouldLogBreakdown: true
  },
  {
    regex: /^.*link\/api\/sendBootstrapData\?id=.*$/,
    name: "ew-bootstrap-call",
    shouldLogBreakdown: true,
    shouldLogDurationInAcrobatPerfEvent: true
  },
  { regex: /^.*AJS\/build\/wasm\_acrobat.*\.wasm$/, name: "wasmPrefetch", shouldLogDurationInAcrobatPerfEvent: true },
  {
    regex: /^.*AJS\/build\/acrobatProxy_we_inline.*\.js$/,
    name: "wasmJsPrefetch",
    shouldLogDurationInAcrobatPerfEvent: true
  }
];

// For progressive rendering
export const FIRST_PAGE_FIRST_BIT_LOAD_TIME = "firstPageFirstBitLoadTime";
export const FIRST_PAGE_LAST_BIT_LOAD_TIME = "firstPageLastBitLoadTime";

// For CNPDF
export const CNPDF_SWITCH_BEGIN = "cnpdfSwitchBegin";
export const CNPDF_SWITCH_END = "cnpdfSwitchEnd";
